import React, { useState, useEffect } from "react";
import Template from "../../../components/Template";
import api from "../../../services/api";
import ConfirmationModal from "../../../components/ConfirmationModal";
import SuccessModal from "../../../components/SuccessModal";
import { useNavigate } from "react-router-dom";
import usePermission from "../../../hooks/usePermission";

const situacoes = ["Ativo", "Inativo"];

const GerenciarTipoObra = () => {
    const navigate = useNavigate();

    // Lista principal de status
    const [objetoList, setObjetoList] = useState([]);

    // Para controlar criação/edição:
    const [isEditMode, setIsEditMode] = useState(false);
    const [editingId, setEditingId] = useState(null);

    const podeCriar = usePermission("CRIAR_CONFIGURACAO_MENU_SUSPENSO_TIPOOBRA");
    const podeAtualizar = usePermission("ATUALIZAR_CONFIGURACAO_MENU_SUSPENSO_TIPOOBRA");
    const podeDeletar = usePermission("DELETAR_CONFIGURACAO_MENU_SUSPENSO_TIPOOBRA");

    // Form principal
    const [formData, setFormData] = useState({
        nome: "",
        descricao: "",
        situacao: "Inativo",
        modulos: [], // array de strings
    });

    // Modais
    const [showConfirmDialog, setShowConfirmDialog] = useState(false);
    const [showConfirmDelete, setShowConfirmDelete] = useState(false);
    const [objetoToDelete, setObjetoToDelete] = useState(null);
    const [showSuccessDialog, setShowSuccessDialog] = useState(false);

    // Carregar status do backend
    useEffect(() => {
        fetchStatus();
    }, []);

    const fetchStatus = async () => {
        try {
            const response = await api.get("/tiposobra"); // Ajuste a rota
            setObjetoList(response.data);
        } catch (error) {
            console.error("Erro ao carregar lista de tipos de obra:", error);
            alert("Erro ao carregar tipos de obra.");
        }
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
    };

    const handleSubmitForm = (e) => {
        e.preventDefault();
        setShowConfirmDialog(true);
    };

    const handleConfirmSubmit = async () => {
        setShowConfirmDialog(false);
        try {
            if (isEditMode && editingId) {
                // PUT
                await api.put(`/tiposobra/${editingId}`, {
                    nome: formData.nome,
                    descricao: formData.descricao,
                    situacao: formData.situacao,
                });
            } else {
                // POST
                await api.post("/tiposobra", {
                    nome: formData.nome,
                    descricao: formData.descricao,
                    situacao: formData.situacao,
                });
            }
            setShowSuccessDialog(true);
            // Recarregar lista
            fetchStatus();
            // Limpar form
            setFormData({
                nome: "",
                descricao: "",
                situacao: "Ativo",
            });
            setIsEditMode(false);
            setEditingId(null);
        } catch (error) {
            console.error("Erro ao salvar tipos de obra:", error);
            alert("Erro ao salvar tipos de obra. Verifique e tente novamente.");
        }
    };

    const handleCancelSubmit = () => {
        setShowConfirmDialog(false);
    };

    // Editar
    const handleEdit = (statusItem) => {
        setIsEditMode(true);
        setEditingId(statusItem.id);
        setFormData({
            nome: statusItem.nome,
            descricao: statusItem.descricao || "",
            situacao: statusItem.situacao || "Ativo",
        });
    };

    // Deletar
    const handleDelete = (statusItem) => {
        setObjetoToDelete(statusItem);
        setShowConfirmDelete(true);
    };

    const handleConfirmDelete = async () => {
        setShowConfirmDelete(false);
        try {
            if (objetoToDelete) {
                await api.delete(`/tiposobra/${objetoToDelete.id}`);
                // recarregar
                fetchStatus();
            }
            setObjetoToDelete(null);
        } catch (error) {
            console.error("Erro ao deletar tipos de obra:", error);
            alert("Erro ao deletar tipos de obra.");
        }
    };

    const handleCancelDelete = () => {
        setShowConfirmDelete(false);
        setObjetoToDelete(null);
    };

    // Cancelar edição
    const handleCancelEdit = () => {
        setIsEditMode(false);
        setEditingId(null);
        setFormData({
            nome: "",
            descricao: "",
            situacao: "Ativo",
        });
    };

    const handleBack = () => {
        navigate('/configuracoes/menus');
    };

    return (
        <Template>
            <div className="w-full px-4 py-6 flex flex-col gap-8">
                {/* Título */}
                <h1 className="text-2xl text-stone-800 font-medium">
                    Gerenciar Tipos Obra
                </h1>

                {/* Formulário de Criação/Edição */}
                <form onSubmit={handleSubmitForm} className="flex flex-col gap-4 bg-white p-4 rounded-md border border-slate-200">
                    <div className="text-stone-600 text-base font-medium">
                        {isEditMode ? "Editar Tipos Obra" : "Cadastrar Novo Tipos Obra"}
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {/* Nome */}
                        <div className="flex flex-col gap-1.5">
                            <label className="text-sm text-slate-700 font-medium">Nome</label>
                            <input
                                type="text"
                                name="nome"
                                value={formData.nome}
                                onChange={handleInputChange}
                                className="px-3 py-2 border border-slate-300 rounded-md text-sm"
                                required
                            />
                        </div>

                        {/* Situação */}
                        <div className="flex flex-col gap-1.5">
                            <label className="text-sm text-slate-700 font-medium">Situação</label>
                            <select
                                name="situacao"
                                value={formData.situacao}
                                onChange={handleInputChange}
                                className="px-3 py-2 border border-slate-300 rounded-md text-sm"
                            >
                                {situacoes.map((sit) => (
                                    <option key={sit} value={sit}>
                                        {sit}
                                    </option>
                                ))}
                            </select>
                        </div>
                    </div>

                    {/* Descrição */}
                    <div className="flex flex-col gap-1.5">
                        <label className="text-sm text-slate-700 font-medium">Descrição</label>
                        <textarea
                            name="descricao"
                            rows={2}
                            value={formData.descricao}
                            onChange={handleInputChange}
                            className="px-3 py-2 border border-slate-300 rounded-md text-sm"
                        />
                    </div>

                    {/* Botões */}
                    <div className="flex items-center gap-4 mt-4">
                        {isEditMode && (
                            <button
                                type="button"
                                onClick={handleCancelEdit}
                                className="px-4 py-2 bg-stone-400 text-white text-sm font-medium rounded-md"
                            >
                                Cancelar
                            </button>
                        )}

                        {podeCriar && (
                            <button
                                type="submit"
                                className="px-4 py-2 bg-stone-600 text-white text-sm font-medium rounded-md"
                            >
                                {isEditMode ? "Salvar Alterações" : "Adicionar Tipos Obra"}
                            </button>
                        )}
                    </div>
                </form>


                <div className="bg-white p-4 rounded-md border border-slate-200">
                    <div className="text-stone-600 text-base font-medium mb-4">
                        Lista de Tipos Obra
                    </div>

                    <div className="overflow-x-auto">
                        <table className="min-w-full text-left border-collapse">
                            <thead>
                                <tr className="bg-stone-100">
                                    <th className="px-4 py-2 border border-slate-200">ID</th>
                                    <th className="px-4 py-2 border border-slate-200">Nome</th>
                                    <th className="px-4 py-2 border border-slate-200">Situação</th>
                                    <th className="px-4 py-2 border border-slate-200">Ações</th>
                                </tr>
                            </thead>
                            <tbody>
                                {objetoList.length > 0 ? (
                                    objetoList.map((st) => (
                                        <tr key={st.id} className="border-b">
                                            <td className="px-4 py-2 border border-slate-200">{st.id}</td>
                                            <td className="px-4 py-2 border border-slate-200">{st.nome}</td>
                                            <td className="px-4 py-2 border border-slate-200">{st.situacao}</td>
                                            <td className="px-4 py-2 border border-slate-200">
                                                {/* Botão Editar */}
                                                {podeAtualizar && (
                                                    <button
                                                        type="button"
                                                        onClick={() => handleEdit(st)}
                                                        className="px-2 py-1 bg-stone-600 text-white rounded-md text-sm mr-2"
                                                    >
                                                        Editar
                                                    </button>
                                                )}
                                                {/* Botão Excluir */}
                                                {podeDeletar && (
                                                    <button
                                                        type="button"
                                                        onClick={() => handleDelete(st)}
                                                        className="px-2 py-1 bg-red-600 text-white rounded-md text-sm"
                                                    >
                                                        Excluir
                                                    </button>
                                                )}
                                            </td>
                                        </tr>
                                    ))
                                ) : (
                                    <tr>
                                        <td
                                            colSpan={5}
                                            className="px-4 py-2 text-center text-slate-700"
                                        >
                                            Nenhum Tipos Obra cadastrado.
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
                {/* Botões de Ação */}
                <div className="flex items-center w-full gap-4">
                    <div className="flex gap-4 ml-auto">
                        <button
                            type="button"
                            className="px-4 py-2 bg-stone-600 text-white text-sm font-medium rounded-md flex items-center gap-2"
                            onClick={handleBack}
                        >
                            <img className="w-4 h-4" src="/images/icon/arrow-left.png" alt="Voltar" />
                            <span>Voltar</span>
                        </button>

                    </div>
                </div>
            </div>

            {/* Confirmar Salvar */}
            <ConfirmationModal
                isVisible={showConfirmDialog}
                onConfirm={handleConfirmSubmit}
                onCancel={handleCancelSubmit}
                message="Deseja salvar este registro?"
            />

            {/* Confirmar Excluir */}
            <ConfirmationModal
                isVisible={showConfirmDelete}
                onConfirm={handleConfirmDelete}
                onCancel={handleCancelDelete}
                message="Deseja apagar este registro?"
                textButtonConfirm="Sim"
            />

            {/* Sucesso */}
            <SuccessModal
                isVisible={showSuccessDialog}
                message="Registro salvo com sucesso!"
                onClose={() => setShowSuccessDialog(false)}
            />
        </Template>
    );
};

export default GerenciarTipoObra;
